import { useState } from 'react'

import DnPrompt from 'src/components/Core/DnPrompt'

type NoContactVoE = { shouldContactEmployer?: boolean | null } | null | undefined

/**
 * Confirms before calling, emailing or starting the voice agent for an
 * employer the applicant has not consented to contact. Only an explicit "yes"
 * counts as consent — a blank answer is "No Consent" too, matching the
 * Consent column, its filter, and the outreach chain (which only starts on
 * `true`). Render `noContactPrompt` once in the caller and wrap each contact
 * action in `guardContact`.
 */
export const useNoContactGuard = () => {
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null)

  const guardContact = (voe: NoContactVoE, action: () => void) => {
    if (voe?.shouldContactEmployer !== true) {
      setPendingAction(() => action)
    } else {
      action()
    }
  }

  const clearPendingAction = () => setPendingAction(null)

  const noContactPrompt = (
    <DnPrompt
      opened={pendingAction != null}
      close={clearPendingAction}
      title="No Consent to Contact This Employer"
      description="The applicant did not give consent to contact this employer. Are you sure you want to proceed with contacting them?"
      confirmText="Contact anyway"
      cancelText="Cancel"
      variant="warning"
      onConfirm={() => pendingAction?.()}
      onCancel={clearPendingAction}
    />
  )

  return { guardContact, noContactPrompt }
}
